import { useState } from 'react';
import { useUsers, useCreateUser, useDeleteUser, useUpdateUser, useReactivateUser } from '@/hooks/useUsers';
import { getRoleName, getRoleColor, availableRoles, UserCreateByAdmin } from '@/services/users';
import { User, UserRole } from '@/types';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import UserPermissionsPanel from '@/components/UserPermissionsPanel';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  User as UserIcon,
  Mail,
  Shield,
  CheckCircle,
  XCircle,
  Copy,
  RefreshCw,
  UserX,
  UserCheck,
  Key,
} from 'lucide-react';

export default function Users() {
  const { user: currentUser } = useAuthStore();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null);
  const [successData, setSuccessData] = useState<{ user: User; temporary_password: string; email_sent: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState<User | null>(null);

  const [formData, setFormData] = useState<UserCreateByAdmin>({
    email: '',
    full_name: '',
    role: 'employee' as UserRole,
  });

  const { data: users, isLoading } = useUsers(showInactive);
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const reactivateUser = useReactivateUser();

  const filteredUsers = users?.filter(user => {
    const matchesSearch =
      user.full_name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  const handleOpenModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        full_name: user.full_name,
        role: user.role,
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        full_name: '',
        role: 'employee',
      });
    }
    setSuccessData(null);
    setCopied(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setSuccessData(null);
    setCopied(false);
    createUser.reset();
    updateUser.reset();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingUser) {
      await updateUser.mutateAsync({
        id: editingUser.id,
        data: {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
        }
      });
      handleCloseModal();
    } else {
      const result = await createUser.mutateAsync(formData);
      setSuccessData(result);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    await deleteUser.mutateAsync(deleteConfirm.id);
    setDeleteConfirm(null);
  };

  const handleReactivate = async (user: User) => {
    await reactivateUser.mutateAsync(user.id);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
          <p className="text-gray-600">Gestiona los usuarios de tu almacén</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-600">Mostrar inactivos</span>
          </label>
        </div>
      </div>

      {/* Lista de usuarios */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : filteredUsers && filteredUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Rol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className={`hover:bg-gray-50 ${!user.is_active ? 'opacity-60' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-700 font-medium">
                            {user.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.full_name}
                            {user.id === currentUser?.id && (
                              <span className="ml-2 text-xs text-primary-600">(Yo)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="h-4 w-4 mr-2 text-gray-400" />
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                          <Shield className="h-3 w-3 mr-1" />
                          {getRoleName(user.role)}
                        </span>
                        {user.module_permissions && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700" title="Permisos personalizados">
                            <Key className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <UserCheck className="h-3 w-3 mr-1" />
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <UserX className="h-3 w-3 mr-1" />
                          Inactivo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex justify-end gap-1">
                        {!user.is_active ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReactivate(user)}
                            title="Reactivar usuario"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        ) : user.id === currentUser?.id ? (
                          // Usuario actual (yo) - no puede editarse a sí mismo
                          <span className="text-xs text-gray-400 italic px-2">Tu usuario</span>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPermissionsUser(user)}
                              title="Permisos"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenModal(user)}
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm(user)}
                              title="Desactivar"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
          </div>
        )}
      </div>

      {/* Modal de crear/editar usuario */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={successData ? 'Usuario Creado!' : editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
        size="lg"
      >
        {successData ? (
          <div className="space-y-6">
            <div className="flex items-center justify-center">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {successData.user.full_name}
              </h3>
              <p className="text-gray-500">ha sido creado exitosamente</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                <UserIcon className="h-4 w-4" />
                Datos de Acceso
              </h4>

              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium">{successData.user.email}</p>
                  </div>
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>

                <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
                  <div>
                    <p className="text-xs text-gray-500">Contrasena Temporal</p>
                    <p className="font-mono text-lg font-bold text-taupe-700">
                      {successData.temporary_password}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(successData.temporary_password)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Copiar contrasena"
                  >
                    {copied ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <Copy className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                successData.email_sent
                  ? 'bg-green-50 text-green-700'
                  : 'bg-amber-50 text-amber-700'
              }`}>
                {successData.email_sent ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Email de bienvenida enviado</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    <span className="text-sm">Email no enviado - comparte la contrasena manualmente</span>
                  </>
                )}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Importante:</strong> El usuario debera cambiar su contrasena en el primer inicio de sesion.
              </p>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleCloseModal}>
                Cerrar
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {(createUser.isError || updateUser.isError) && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {(() => {
                  const detail = (createUser.error as any)?.response?.data?.detail ||
                    (updateUser.error as any)?.response?.data?.detail;
                  if (!detail) return 'Error al guardar el usuario';
                  if (typeof detail === 'string') return detail;
                  if (Array.isArray(detail)) return detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
                  return JSON.stringify(detail);
                })()}
              </div>
            )}

            <div className="space-y-4">
              <Input
                label="Nombre Completo *"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Ej: Juan Perez"
                required
              />

              {editingUser ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <p className="px-4 py-2.5 bg-gray-100 rounded-xl text-gray-600">
                    {formData.email}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    El email no puede modificarse
                  </p>
                </div>
              ) : (
                <Input
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="usuario@almacen.com"
                  required
                />
              )}

              {/* No mostrar selector de rol si está editando su propio perfil */}
              {!(editingUser && editingUser.id === currentUser?.id) && (
                <>
                  <Select
                    label="Rol *"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                    options={availableRoles.map(r => ({ value: r.value, label: r.label }))}
                    required
                  />

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Roles disponibles:</strong>
                    </p>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1">
                      <li><strong>Administrador:</strong> Acceso completo a todas las funciones</li>
                      <li><strong>Empleado:</strong> Acceso según los permisos que le asignes</li>
                    </ul>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="secondary" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={createUser.isPending || updateUser.isPending}
              >
                {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Modal de confirmacion de eliminacion */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Desactivar Usuario"
        size="sm"
      >
        {deleteConfirm && (
          <div className="space-y-4">
            <p className="text-gray-600">
              Estas seguro de desactivar a <strong>{deleteConfirm.full_name}</strong>?
            </p>
            <p className="text-sm text-amber-600">
              El usuario no podra iniciar sesion pero podras reactivarlo mas tarde.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                loading={deleteUser.isPending}
              >
                Desactivar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de permisos */}
      <Modal
        isOpen={!!permissionsUser}
        onClose={() => setPermissionsUser(null)}
        title="Configurar Permisos"
        size="lg"
      >
        {permissionsUser && (
          <UserPermissionsPanel
            user={permissionsUser}
            onClose={() => setPermissionsUser(null)}
          />
        )}
      </Modal>
    </div>
  );
}
